import React, { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Loader2, CheckCircle2 } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";
import { SectionLabel } from "@/components/shared";
import { CONTACT, STAFF_CONTACTS } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-none border border-white/15 bg-zinc-900 px-4 py-3 font-body text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-neon-green focus:ring-1 focus:ring-neon-green";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative overflow-hidden pb-16 pt-40 md:pt-48">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionLabel>Get In Touch</SectionLabel>
          <h1 className="font-heading text-6xl uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[8rem]">
            <MaskLines lines={["Let's", <span className="text-neon-green">Talk</span>]} />
          </h1>
        </div>
      </section>

      <section className="relative z-10 bg-obsidian pb-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div>
              <h2 className="font-heading text-3xl uppercase text-white">Contact Details</h2>
              <p className="mt-3 font-body text-sm text-white/50">Editable placeholders — real details will be swapped in once supplied.</p>
              <ul className="mt-8 space-y-6">
                {[
                  [MapPin, "Area", CONTACT.serviceArea],
                ].map(([Icon, label, val]) => (
                  <li key={label} className="flex items-start gap-4 border-b border-white/10 pb-6">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-neon-green/40 text-neon-green">
                      <Icon size={20} strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="font-body text-xs font-bold uppercase tracking-[0.25em] text-neon-green">{label}</div>
                      <div className="mt-1 font-body text-base text-white/80">{val}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8 space-y-6" data-testid="staff-contacts">
                {STAFF_CONTACTS.map((s) => (
                  <div key={s.email} className="border border-white/10 bg-surface p-5">
                    <div className="font-heading text-xl uppercase text-white">{s.name}</div>
                    <a href={`mailto:${s.email}`} className="mt-2 flex items-center gap-2 font-body text-sm text-white/70 hover:text-neon-green"><Mail size={16} strokeWidth={2.5} className="text-neon-green" /> {s.email}</a>
                    <a href={`tel:${s.phone.replace(/[^0-9]/g, "")}`} className="mt-1 flex items-center gap-2 font-body text-sm text-white/70 hover:text-neon-green"><Phone size={16} strokeWidth={2.5} className="text-neon-green" /> {s.phone}</a>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex gap-4">
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="flex h-11 w-11 items-center justify-center border border-white/15 text-white/70 hover:border-neon-green hover:text-neon-green"><Instagram size={20} strokeWidth={2.5} /></a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            {done ? (
              <div data-testid="contact-success" className="border border-neon-green/40 bg-zinc-900 p-10 text-center neon-glow">
                <CheckCircle2 size={44} strokeWidth={2.5} className="mx-auto text-neon-green" />
                <h3 className="mt-4 font-heading text-3xl uppercase text-white">Message Sent</h3>
                <p className="mt-2 font-body text-sm text-white/60">Thanks for reaching out — we'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5 border border-white/10 bg-surface p-8">
                {error && <div data-testid="contact-error" className="border border-red-500/50 bg-red-500/10 px-4 py-3 font-body text-sm text-red-300">{error}</div>}
                <input required data-testid="contact-name" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                <input required type="email" data-testid="contact-email" className={inputCls} placeholder="Your email" value={form.email} onChange={set("email")} />
                <textarea required rows={5} data-testid="contact-message" className={inputCls} placeholder="How can we help?" value={form.message} onChange={set("message")} />
                <button disabled={loading} data-testid="contact-submit" className="flex w-full items-center justify-center bg-neon-green py-4 font-heading text-lg uppercase tracking-widest text-black disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={22} /> : "Send Message"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
